import React from 'react'
import { captureException } from '@sentry/react'
import { InferType } from 'yup'
import { useMutation } from '@tanstack/react-query'
import { Modal } from '../../../../../components/common/Modal'
import { ModalHeader } from '../../../../../components/common/Modal/ModalHeader'
import { ModalBody } from '../../../../../components/common/Modal/ModalBody'
import { useToastNotifications } from '../../../../../hooks/useToastNotification'
import { ToastType } from '../../../../../components/common/Toast/toast-type'
import { useAppTranslation } from '../../../../../hooks/useAppTranslation'
import { SetMasterAdminModalContent } from './SetMasterAdminModalContent'
import { adminSetMasterAdminSchema } from '../../../../../forms/schemas/admin_set_master_admin'
import { useAdminSociety } from '../useAdminSociety'
import { patchSociety } from '../../../../../api/admin'

interface SetMasterAdminModalProps {
  show: boolean
  close: () => void
  societyId?: string
  adminList?: string[]
}

export const SetMasterAdminModal = ({
  show,
  close,
  societyId,
  adminList,
}: SetMasterAdminModalProps): React.ReactNode => {
  const { setToastNotification } = useToastNotifications()
  const { translate } = useAppTranslation()
  const { adminSociety } = useAdminSociety(societyId)

  const { mutate: mutateSetMasterAdmin } = useMutation({
    mutationFn: patchSociety,
    onSuccess: () => {
      setToastNotification(
        ToastType.SUCCESS,
        translate('Master admin uppdaterad')
      )
      close()
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('flashMessage.somethingWentWrongError')
      )
    },
  })
  const onSubmit = async (
    data: InferType<typeof adminSetMasterAdminSchema>
  ): Promise<void> => {
    societyId && mutateSetMasterAdmin({ societyId, data })
  }

  const onError = (error: unknown): void => {
    captureException(error)
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.somethingWentWrongError')
    )
  }

  return (
    <Modal show={show}>
      {{
        header: (
          <ModalHeader onClose={close}>Uppdatera master admin</ModalHeader>
        ),
        body: (
          <ModalBody className="h-[400px] overflow-y-auto">
            {adminSociety && (
              <SetMasterAdminModalContent
                onSubmit={onSubmit}
                onError={onError}
                onClose={close}
                society={adminSociety}
                adminsList={adminList}
              />
            )}
          </ModalBody>
        ),
      }}
    </Modal>
  )
}
